import React, { useEffect, useState, useMemo } from 'react';
import {
  CircularProgress,
  Alert,
  Chip,
  IconButton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Button,
  Box,
  Tabs,
  Tab,
  TextField,
  InputAdornment,
} from '@mui/material';
import BlockIcon from '@mui/icons-material/Block';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import SearchIcon from '@mui/icons-material/Search';
import { type GridColDef } from '@mui/x-data-grid';
import { DataGrid, PageTitle, ListLayoutPanel, AddButton, GridEditButton } from '../../../components/common';
import { EditArticulo } from './components';
import { type Articulo, type ArticuloRequest } from './types/articulo';
import { useArticuloStore } from './store';

const ESTADO_ACTIVO = 1;
const ESTADO_INACTIVO = 2;

const ArticulosPage: React.FC = () => {
  const {
    articulos,
    loading,
    error,
    selectedArticulo,
    dialogOpen,
    fetchArticulos,
    saveArticulo,
    changeEstado,
    setSelectedArticulo,
    openDialog,
    closeDialog,
    clearError
  } = useArticuloStore();

  const [confirmDialog, setConfirmDialog] = useState<{ open: boolean; articulo: Articulo | null }>({
    open: false,
    articulo: null,
  });
  const [selectedTab, setSelectedTab] = useState(0);
  const [searchTerm, setSearchTerm] = useState('');

  // Filter by tab and search
  const filteredArticulos = useMemo(() => {
    let filtered = articulos;

    // Filter by tab
    switch (selectedTab) {
      case 0: // Activos
        filtered = filtered.filter(a => a.idEstado === ESTADO_ACTIVO);
        break;
      case 1: // No Activos
        filtered = filtered.filter(a => a.idEstado !== ESTADO_ACTIVO);
        break;
      // case 2: Todos - no filter
    }

    // Filter by search
    if (searchTerm.trim()) {
      const term = searchTerm.toLowerCase().trim();
      filtered = filtered.filter(a =>
        a.descripcion?.toLowerCase().includes(term) ||
        a.codigoArticulo?.toLowerCase().includes(term) ||
        a.marcaNombre?.toLowerCase().includes(term)
      );
    }

    return filtered;
  }, [articulos, selectedTab, searchTerm]);

  // Counts for tabs
  const countActivos = articulos.filter(a => a.idEstado === ESTADO_ACTIVO).length;
  const countInactivos = articulos.filter(a => a.idEstado !== ESTADO_ACTIVO).length;

  const handleToggleEstado = (articulo: Articulo) => {
    setConfirmDialog({ open: true, articulo });
  };

  const handleConfirmToggle = async () => {
    if (!confirmDialog.articulo) return;
    const { idArticulo, idEstado } = confirmDialog.articulo;
    const newEstado = idEstado === ESTADO_ACTIVO ? ESTADO_INACTIVO : ESTADO_ACTIVO;
    await changeEstado(idArticulo, newEstado);
    setConfirmDialog({ open: false, articulo: null });
  };

  const columns: GridColDef[] = [
    { field: 'codigoArticulo', headerName: 'Código', width: 120 },
    { field: 'descripcion', headerName: 'Descripción', width: 250, flex: 1 },
    { field: 'marcaNombre', headerName: 'Marca', width: 150 },
    {
      field: 'estadoNombre',
      headerName: 'Estado',
      width: 120,
      renderCell: (params) => {
        const isActivo = params.row.idEstado === ESTADO_ACTIVO;
        return (
          <Chip
            label={params.value || 'Sin estado'}
            color={isActivo ? 'success' : 'default'}
            size="small"
            variant={isActivo ? 'filled' : 'outlined'}
          />
        );
      },
    },
    {
      field: 'actions',
      headerName: 'Acciones',
      width: 120,
      sortable: false,
      filterable: false,
      disableColumnMenu: true,
      align: 'center',
      renderCell: (params) => (
        <Box sx={{ display: 'flex', gap: 0.5 }}>
          <GridEditButton
            onClick={() => handleEdit(params.row.idArticulo)}
            ariaLabel={`Editar artículo ${params.row.descripcion}`}
          />
          <Tooltip title={params.row.idEstado === ESTADO_ACTIVO ? 'Anular' : 'Activar'}>
            <IconButton
              size="small"
              onClick={() => handleToggleEstado(params.row)}
              color={params.row.idEstado === ESTADO_ACTIVO ? 'warning' : 'success'}
            >
              {params.row.idEstado === ESTADO_ACTIVO ? <BlockIcon fontSize="small" /> : <CheckCircleIcon fontSize="small" />}
            </IconButton>
          </Tooltip>
        </Box>
      ),
    },
  ];

  useEffect(() => {
    fetchArticulos();
  }, [fetchArticulos]);

  const handleEdit = (idArticulo: number) => {
    const articulo = articulos.find(a => a.idArticulo === idArticulo) || null;
    setSelectedArticulo(articulo);
    openDialog();
  };

  const handleCreate = () => {
    setSelectedArticulo(null);
    openDialog();
  };

  const handleClose = () => {
    closeDialog();
  };

  const handleSave = async (updatedArticulo: ArticuloRequest) => {
    try {
      await saveArticulo(updatedArticulo);
    } catch (error) {
      console.error('Error saving articulo:', error);
    }
  };

  if (loading) {
    return (
      <ListLayoutPanel>
        <PageTitle
          title="Artículos"
          subtitle="Gestión de artículos"
          actions={
            <AddButton
              text="Nuevo Artículo"
              onClick={handleCreate}
            />
          }
        />
        <CircularProgress />
      </ListLayoutPanel>
    );
  }

  if (error) {
    return (
      <ListLayoutPanel>
        <PageTitle
          title="Artículos"
          subtitle="Gestión de artículos"
          actions={
            <AddButton
              text="Nuevo Artículo"
              onClick={handleCreate}
            />
          }
        />
        <Alert
          severity="error"
          onClose={clearError}
        >
          {error}
        </Alert>
      </ListLayoutPanel>
    );
  }

  return (
    <ListLayoutPanel>
      <PageTitle
        title="Artículos"
        subtitle="Gestión de artículos"
        actions={
          <AddButton
            text="Nuevo Artículo"
            onClick={handleCreate}
          />
        }
      />

      <Box sx={{ mb: 2 }}>
        <TextField
          placeholder="Buscar por descripción, código o marca..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          size="small"
          fullWidth
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon color="action" />
              </InputAdornment>
            ),
          }}
          sx={{
            maxWidth: 500,
            '& .MuiOutlinedInput-root': {
              backgroundColor: '#f9fafb',
              borderRadius: '0.5rem',
            },
          }}
        />
      </Box>

      <Tabs
        value={selectedTab}
        onChange={(_, newValue) => setSelectedTab(newValue)}
        sx={{ mb: 2 }}
      >
        <Tab label={`Activos (${countActivos})`} />
        <Tab label={`No Activos (${countInactivos})`} />
        <Tab label={`Todos (${articulos.length})`} />
      </Tabs>

      <DataGrid
        columns={columns}
        rows={filteredArticulos}
        getRowId={(row: Articulo) => row.idArticulo}
      />

      <EditArticulo
        open={dialogOpen}
        articuloIdArticulo={selectedArticulo?.idArticulo || null}
        onClose={handleClose}
        onSave={handleSave}
        title={selectedArticulo ? "Editar Artículo" : "Nuevo Artículo"}
      />

      <Dialog
        open={confirmDialog.open}
        onClose={() => setConfirmDialog({ open: false, articulo: null })}
      >
        <DialogTitle>
          {confirmDialog.articulo?.idEstado === ESTADO_ACTIVO ? 'Anular Artículo' : 'Activar Artículo'}
        </DialogTitle>
        <DialogContent>
          <DialogContentText>
            {confirmDialog.articulo?.idEstado === ESTADO_ACTIVO
              ? `¿Está seguro que desea anular el artículo "${confirmDialog.articulo?.descripcion}"?`
              : `¿Está seguro que desea activar el artículo "${confirmDialog.articulo?.descripcion}"?`
            }
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmDialog({ open: false, articulo: null })}>
            Cancelar
          </Button>
          <Button
            onClick={handleConfirmToggle}
            color={confirmDialog.articulo?.idEstado === ESTADO_ACTIVO ? 'warning' : 'success'}
            variant="contained"
          >
            {confirmDialog.articulo?.idEstado === ESTADO_ACTIVO ? 'Anular' : 'Activar'}
          </Button>
        </DialogActions>
      </Dialog>
    </ListLayoutPanel>
  );
};

export default ArticulosPage;
